import {
    TransactionDetailRow,
    TransactionArrow,
    TransactionFeeSection,
    TransactionDialogContent,
    TransactionSection,
} from "@/components/transaction";
import { TokenAmount } from "@/components/shared";
import { useDepositTransaction, usePriceStore } from "@/hooks";
import { COMMON_TOKEN_IDS, TOKEN_IDS, type TokenId } from "@/types";
import { getGatewayContractAddress } from "@/config";
import { useTokenApprovalV2 } from "@/hooks/transaction/useTokenApprovalV2";
import { mainnet } from "viem/chains";
import { useCallback, useMemo } from "react";
import { ActionText } from "@/constants/text.enum";
import { safaryTrackDepositSuccess } from "@/services/analytics/safary.service";
import { useAccount } from "wagmi";
import { useTrackingConsent } from "../../hooks/shared/tracking-consent/useTrackingConsent";
import { Seperator } from "../shared/Seperator";
import { getTransactionLoadingState } from "./transaction-loading.utils";

const CONFIRM_TEXT = "Confirm Stake";

export interface DepositTransactionProps {
    depositAmount: number;
    depositTokenId: TokenId;
    onClose?: () => void;
}

export const DepositTransactionDialog = ({ depositAmount, depositTokenId, onClose }: DepositTransactionProps) => {
    const { address } = useAccount();

    const {
        gasPriceUSD,
        executeDepositTransaction,
        isLoading: isDepositTransactionLoading,
        apy,
        siUSDExchange,
        isExecuting,
    } = useDepositTransaction({
        depositAmount,
        depositTokenId,
    });

    const { price } = usePriceStore(depositTokenId);

    const isDirectStake = depositTokenId === TOKEN_IDS.IUSD;

    const receiveAmount = useMemo(() => Number(depositAmount) * Number(price ?? 0), [depositAmount, price]);

    const MINT_TEXT = useMemo(
        () => (
            <TokenAmount
                amount={depositAmount}
                tokenId={depositTokenId}
                size="md"
                slotProps={{
                    amount: { className: "text-dark" },
                    symbol: { className: "text-dark " },
                }}
            />
        ),
        [depositAmount, depositTokenId],
    );

    const MINT_RECEIVE_TEXT = useMemo(
        () => (
            <TokenAmount
                amount={receiveAmount.toFixed(8)}
                tokenId={TOKEN_IDS.IUSD}
                size="md"
                slotProps={{
                    amount: { className: "text-dark" },
                    symbol: { className: "text-dark w-[20px]" },
                }}
            />
        ),
        [receiveAmount],
    );

    const STAKE_TEXT = useMemo(
        () => (
            <TokenAmount
                amount={receiveAmount.toFixed(8)}
                tokenId={TOKEN_IDS.IUSD}
                size="md"
                slotProps={{
                    amount: { className: "text-dark" },
                    symbol: { className: "text-dark" },
                }}
            />
        ),
        [receiveAmount],
    );

    const APY_TEXT = `${apy}% APY`;

    const RECEIVE_STAKED_TEXT = useMemo(
        () => (
            <TokenAmount
                amount={`~${(receiveAmount / siUSDExchange).toFixed(8)}`}
                tokenId={TOKEN_IDS.SIUSD}
                size="md"
                slotProps={{
                    amount: { className: "text-dark" },
                    symbol: { className: "text-dark " },
                }}
            />
        ),
        [receiveAmount, siUSDExchange],
    );

    const { isApproved, isApprovalLoading, allowance, withApproval, approvalLoadingText } = useTokenApprovalV2({
        tokenId: depositTokenId,
        amount: depositAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const { canTrack } = useTrackingConsent();

    const handleConfirm = useCallback(async () => {
        try {
            const hasExecuted = await withApproval(
                async () => {
                    await executeDepositTransaction();
                    return true;
                },
                { isExecuting, amount: depositAmount },
            );

            if (!hasExecuted) return;

            try {
                canTrack &&
                    address &&
                    safaryTrackDepositSuccess({
                        tokenIn: depositTokenId,
                        tokenOut: COMMON_TOKEN_IDS.SIUSD,
                        tokenAmountIn: depositAmount,
                        tokenAmountOut: depositAmount * siUSDExchange,
                        walletAddress: address,
                    });
            } catch (e) {
                console.error("[Safary] Failed to track deposit success", e);
            }

            onClose?.();
        } catch (e) {
            console.error(e);
        }
    }, [
        withApproval,
        executeDepositTransaction,
        onClose,
        canTrack,
        address,
        depositTokenId,
        depositAmount,
        siUSDExchange,
    ]);

    const { isLoading, loadingText } = getTransactionLoadingState({
        approvalLoadingText,
        isApprovalLoading,
        isExecuting,
        actionLoadingText: ActionText.STAKING,
        isTransactionLoading: isDepositTransactionLoading,
    });
    const isDisabled = isLoading || !depositAmount || !depositTokenId;

    const isUsdt = depositTokenId === TOKEN_IDS.USDT;
    const isUsdtRevocationNeeded = isUsdt && !isApproved && allowance !== 0n;

    return (
        <TransactionDialogContent
            className="w-[420px]"
            onClose={onClose}
            onConfirm={handleConfirm}
            isLoading={isLoading}
            loadingText={loadingText}
            confirmText={CONFIRM_TEXT}
            disabled={isDisabled}
            message={
                isUsdtRevocationNeeded
                    ? "USDT requires current allowance is revoked before it can be increased."
                    : undefined
            }
        >
            <TransactionSection>
                {!isDirectStake && (
                    <div className="flex flex-col gap-2">
                        <TransactionDetailRow label="Mint" value={MINT_TEXT} />
                        <TransactionArrow className="text-[rgba(68,68,68,1)]" />
                        <TransactionDetailRow label="" value={MINT_RECEIVE_TEXT} />
                        <Seperator />
                    </div>
                )}
                <TransactionDetailRow label="Stake" value={STAKE_TEXT} className="mb-2" />
                <TransactionDetailRow
                    label="Rate"
                    value={
                        <span className="inline-flex">
                            {APY_TEXT}
                            <sup className="mt-4">*</sup>
                        </span>
                    }
                    slotProps={{
                        value: { className: "text-secondary" },
                    }}
                />
                <Seperator />
                <TransactionDetailRow label="Receive" value={RECEIVE_STAKED_TEXT} />
            </TransactionSection>
            <Seperator />
            <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
        </TransactionDialogContent>
    );
};
